/**
 * Room booking (/book) page copy — imported into main translations (en / de / fr).
 */

import type { RoomTag } from "./room-metadata";
import {
  bookRoomLadderCopyDe,
  bookRoomLadderCopyEn,
  bookRoomLadderCopyFr,
  bookRoomTagsDe,
  bookRoomTagsEn,
  bookRoomTagsFr,
  bookUpsellsCopyDe,
  bookUpsellsCopyEn,
  bookUpsellsCopyFr,
  type BookRoomLadderCopy,
  type BookUpsellCopy,
} from "./book-room-upsell-content-i18n";

export type { BookRoomLadderId, BookUpsellCopyId } from "./book-room-upsell-content-i18n";

/** Canonical `Room.bedding.configuration` strings from `rooms.ts` (localization keys). */
export type RoomBeddingConfigKey =
  | "1 Single Bed"
  | "1 Double Bed"
  | "2 Single Beds"
  | "1 Double + 1 Single Bed or 3 Single Beds"
  | "1 King Bed"
  | "1 Double Bed or 2 Twin Beds"
  | "3 Double Beds + Sofa Bed";

export interface BookPageTranslations {
  directBookerDiscountLabel: string;
  signature: {
    cancelTimeLabel: string;
    cancelTimeStrip: string;
    pastDeadlinePerkLine: string;
    bannerSelectDates: string;
    bannerFullyFlexibleUntil: string;
    summaryFreeUntil: string;
    summarySelectDates: string;
    summaryChooseSignature: string;
    taglineMobileNoDates: string;
    taglineMobileUntil: string;
    /** Short mobile line when Signature deadline has passed */
    taglineMobilePastDeadline: string;
    badgeRecommended: string;
    badgeFreeCancellation: string;
    badgeLowestPrice: string;
    standardPolicyStrip: string;
  };
  ratePlans: {
    standard: { name: string; included: string[] };
    signature: {
      name: string;
      /** First Signature bullet (checkmark): e.g. “All Standard Benefits”. */
      breakfastWifiLine: string;
      included: string[];
      /** Main line for complimentary thermal / wellness package on Signature card. */
      thermalPackageWorthTemplate: string;
      /** Shown under the thermal package line on the Signature rate card. */
      thermalPackageFacilitiesLine: string;
    };
  };
  filters: Record<
    | "All"
    | "Eiger, Mönch & Jungfrau View"
    | "Lake View"
    | "Beatenberg View"
    | "Balcony"
    | "No balcony"
    | "Suites",
    string
  >;
  viewBadges: Record<
    | "Eiger, Mönch & Jungfrau View"
    | "Lake View"
    | "Beatenberg View"
    | "Village Side"
    | "Village View"
    | "Standard View",
    string
  >;
  addonFilters: Record<"All" | "Spa & Massage" | "Dining" | "Romance", string>;
  roomCard: {
    balconyBadge: string;
    privateBalcony: string;
    alpineComfort: string;
    elegantSeating: string;
    ensuiteCoffee: string;
    showRatesAndOffers: string;
    hideRatesAndOffers: string;
    notAvailableDates: string;
    from: string;
    perNight: string;
    perNightSlash: string;
    /** Labels for `Room.bedding.configuration` (English keys from `rooms.ts`). */
    beddingLabels: Record<RoomBeddingConfigKey, string>;
    /** Book page only: Heritage Family Suite bed line (overrides `bedding.configuration` label). */
    familySuiteBedding: string;
  };
  rateCard: {
    pricingNotAvailable: string;
    selectDatesForPricing: string;
    totalForStay: string;
    nightStayOne: string;
    nightStayMany: string;
    /** e.g. "- Save {{amount}}" where amount is already localized currency. */
    youSaved: string;
    perNightLabel: string;
    previously: string;
    previouslyParen: string;
    selected: string;
    selectRate: string;
  };
  validation: {
    firstNameRequired: string;
    lastNameRequired: string;
    emailRequired: string;
    emailInvalid: string;
    telephoneRequired: string;
    telephoneTooShort: string;
    telephoneTooLong: string;
    telephoneInvalidLength: string;
    telephoneLengthInvalid: string;
    telephoneInvalid: string;
    bookerFirstNameRequired: string;
    bookerLastNameRequired: string;
    bookerEmailRequired: string;
    bookerEmailInvalid: string;
    bookerTelephoneRequired: string;
    completeGuestDetails: string;
    completePaymentFields: string;
    expirationRequired: string;
    expirationFormat: string;
    expirationValid: string;
    monthRange: string;
    cardExpired: string;
    holderNameRequired: string;
  };
  errors: {
    loadReservation: string;
    loadAvailabilityConfig: string;
    loadAvailabilityAge: string;
    occupancyInvalid: string;
    retrieveAvailability: string;
    availabilityChanged: string;
    createReservation: string;
    paymentNotLinked: string;
    configError: string;
    verifyCard: string;
    loadReservationShort: string;
    cardNotAuthorized: string;
    cardDeclined: string;
    authCanceled: string;
    cardVerificationFailed: string;
    cardVerificationFailedGeneric: string;
    roomMappingMissing: string;
    finalPriceProceed: string;
    /** When getReservationPrice returns no message */
    verifyFinalPrice: string;
    /** Multi-room: response count or Identifier does not match request */
    reservationPriceAlignment: string;
  };
  policies: {
    charges: string;
    ratesPerNight: string;
    nights: string;
    night: string;
    selectRoomRate: string;
    roomTotal: string;
    addOns: string;
    total: string;
    includingAllTaxes: string;
    guaranteeTitle: string;
    guaranteeBody: string;
    cancellationTitle: string;
    brsSignaturePrefix: string;
    checkInTitle: string;
    checkInBody: string;
    childrenTitle: string;
    childrenBody: string;
    petsTitle: string;
    petsBody: string;
    includingTaxesFees: string;
  };
  bar: {
    arrivalLong: string;
    arrivalShort: string;
    departureLong: string;
    departureShort: string;
    selectDate: string;
    guest: string;
    guests: string;
    /** Bar summary: singular after "1" (e.g. Room) */
    room: string;
    rooms: string;
    checkAvailability: string;
    organicBedding: string;
    ensuiteBath: string;
    adults: string;
    children: string;
    /** Guest counter row: singular after "1" (e.g. Adult) */
    guestCounterAdultSingular: string;
    guestCounterAdultPlural: string;
    guestCounterChildSingular: string;
    guestCounterChildPlural: string;
    /** Subtext under children count, e.g. age range */
    guestCounterChildAgeHint: string;
    /** Per-room title in criteria, e.g. "Room {{n}}" — {{n}} is replaced with 1-based index */
    roomLabelTemplate: string;
    addRoom: string;
    /** Remove a guest row (room 2+) */
    removeRoom: string;
    maxRoomsTitle: string;
    maxRoomsContactHtml: string;
  };
  /** Shown when leaving /book with an active booking session (dates confirmed, etc.). */
  leaveGuard: {
    warning: string;
    question: string;
    /** Stay on page (primary / gold in UI). */
    stay: string;
    /** Leave and discard session (outline in UI). */
    leave: string;
  };
  criteriaOverlay: {
    title: string;
    dates: string;
    guests: string;
  };
  /** Split occupancy toasts (overlay first apply + post-search bar guidance). */
  splitOccupancy: {
    /** Toast A: {{n}} = total room count after split */
    dividedAcrossRoomsToast: string;
    /** Toast B: {{addRoom}} = localized + Add room label */
    guidanceUseAddRoomToast: string;
    splitBlockedMaxRooms: string;
    splitBlockedInsufficientAdults: string;
    /** Bar “Check availability” before first overlay search (title + toast). */
    barApplyRequiresOverlayFirst: string;
    /** Inline alert when split would exceed max rooms (body line before contact). */
    splitOverflowInlineLead: string;
    /** Short alert title when split fails for insufficient adults. */
    splitInsufficientAdultsTitle: string;
    /** Booking-bar dropdown: one short line when any row has T > 2 (first two popover opens). */
    barSplitGuestHintCompact: string;
  };
  confirmation: {
    backHome: string;
    title: string;
    subtitle: string;
    loadingTitle: string;
    loadingHint: string;
    contactWithNumber: string;
    confirmedTitle: string;
    confirmedBodyPrefix: string;
    confirmedBodySuffix: string;
    numbersPrefix: string;
    stayDetails: string;
    rooms: string;
    datesNights: string;
    guestsLabel: string;
    adult: string;
    adults: string;
    child: string;
    children: string;
    additionalServices: string;
    servicesIncluded: string;
    total: string;
    reservationCreated: string;
    reservationCreatedBodyPrefix: string;
    reservationCreatedBodySuffix: string;
    cardNotAvailableTitle: string;
    cardNotAvailableBody: string;
    contactUs: string;
    verifyingPrice: string;
    priceChanged: string;
    finalPrice: string;
    amountChargeOnConfirmation: string;
    paymentDetails: string;
    paymentSecureNote: string;
    nameOnCard: string;
    validity: string;
    tryAnotherCardHint: string;
    tryAnotherCard: string;
    processing: string;
    confirmBooking: string;
    roomFallbackName: string;
    /** "No. {{number}}" — per-reservation heading on confirmation */
    confirmationNumberShort: string;
    /** Booked product UUID not mapped in our upsell catalog */
    unknownProduct: string;
  };
  steps: {
    chooseViewTitle: string;
    /** "Plan your stay ({{current}}/{{total}})" — legacy; heading now uses stayStepRoomProgress + IoPeople */
    chooseViewTitleWithProgress: string;
    /** "{{label}} · {{occupancy}}" — legacy subtitle tokens */
    chooseViewActiveRoomSubtitle: string;
    /** "Room {{current}} of {{total}}" — inside plan step parenthetical (with IoPeople + occupancy) */
    stayStepRoomProgress: string;
    chooseViewSubtitle: string;
    noRoomsMatch: string;
    enhanceTitle: string;
    /** "Enhance your stay ({{current}}/{{total}})" — multi-room enhance loop */
    enhanceTitleWithProgress: string;
    enhanceSubtitle: string;
    /** "{{label}} · {{occupancy}}" — active enhance room (same tokens as plan subtitle) */
    enhanceActiveRoomSubtitle: string;
    /** Multi-room plan/enhance: `aria-label` for occupancy trigger; `{{label}}` = stay row label */
    editStayRoomOccupancyAria: string;
    /** Dialog title when editing occupancy on narrow viewports; `{{label}}` = stay row label */
    editStayRoomOccupancyTitle: string;
    noAddonsCategory: string;
    detailsTitle: string;
    detailsSubtitle: string;
    bookingForSelf: string;
    bookingForOther: string;
    processingReservation: string;
    doNotClose: string;
    yourDetails: string;
    guestDetailsSection: string;
    firstName: string;
    lastName: string;
    email: string;
    emailHint: string;
    phone: string;
    nationality: string;
    specialRequests: string;
    marketingOptIn: string;
    marketingOptInBooker: string;
    backAria: string;
  };
  summary: {
    title: string;
    chooseRoom: string;
    chooseRate: string;
    edit: string;
    stayEnhancements: string;
    /** Stay summary: resort amenity discount line when no rate plan chosen yet */
    enhancementDiscountNoRate: string;
    /** Stay summary: when Signature rate is selected */
    enhancementDiscountSignature: string;
    /** Stay summary: when Standard rate is selected */
    enhancementDiscountStandard: string;
    noAddons: string;
    exclusiveBenefits: string;
    benefit1Title: string;
    benefit1Desc: string;
    benefit2Title: string;
    benefit2Desc: string;
    benefit3Title: string;
    benefit3Desc: string;
    continue: string;
    back: string;
    selectRoomRateContinue: string;
    selected: string;
    selectRatePlan: string;
    /** Dismiss dimmed overlay behind expanded mobile booking summary */
    closeExpandedSummaryAria: string;
    /** Sticky list heading when booking multiple rooms (plan step) */
    multiRoomPlanStickyHeading: string;
    /** Short label when a room has no category/rate yet */
    roomPlanNotSelected: string;
    /** Collapsed mobile summary: multi-room plan/addons — current/total stay rooms */
    mobileMultiRoomStayProgress: string;
    /** Collapsed mobile bar on details (multi-room): {{count}} {{nightsLabel}}; UI shows moon icon instead of label */
    mobileCollapsedDetailsTotalNightsLine: string;
    /** Beside free-cancellation pill; {{date}} = short month + day (locale / Zurich TZ) */
    mobileFreeCancellationPillTillDate: string;
    /** Expanded mobile sheet: top-right when all stays planned (→ details / checkout) */
    expandedSheetCheckoutLink: string;
    /** Multi-room stay cards: aria for expand/collapse add-on line items */
    toggleMultiRoomAddonsAria: string;
  };
  bestRateGuarantee: {
    headline: string;
    subline: string;
    /** Shown underlined below the subline on mobile/tablet only (hidden on `book` layout). */
    learnMore: string;
  };
  carousel: {
    goToImage: string;
    viewFullScreen: string;
    photosTitle: string;
    photosDescription: string;
  };
  /** Ladder room titles & card blurbs by `sourceRoom.id` */
  roomLadder: BookRoomLadderCopy;
  upsells: BookUpsellCopy;
  roomTags: Record<RoomTag, string>;
  upsellCategoryLabels: Record<
    "Dining" | "Romance" | "Spa & Massage" | "Sauna & Thermal",
    string
  >;
  upsellCard: {
    decreaseAria: string;
    increaseAria: string;
    add: string;
    added: string;
    /** Shown for Mews PerPerson / PerPersonPerTimeUnit products on the enhance step */
    pricedForRoomGuests: string;
  };
}

export const bookPageTranslationsEn: BookPageTranslations = {
  directBookerDiscountLabel: "Direct booker discount",
  signature: {
    cancelTimeLabel: "12:00 am CET",
    cancelTimeStrip: "12:00 am",
    pastDeadlinePerkLine:
      "10% off spa & resort extras during your stay — no pre-booking required.",
    bannerSelectDates: "Select your stay dates to see flexible cancellation details (12:00 am).",
    bannerFullyFlexibleUntil: "Fully flexible cancellation until {{date}}, {{time}}",
    summaryFreeUntil: "Free cancellation until {{date}}, {{time}}",
    summarySelectDates: "Select check-in dates to see the free cancellation deadline (12:00 am CET).",
    summaryChooseSignature: "Cancellation terms depend on the rate you select. Choose Signature to see free cancellation details.",
    taglineMobileNoDates: "Free cancellation — select dates",
    taglineMobileUntil: "Free cancellation until {{date}}",
    taglineMobilePastDeadline: "10% off spa & extras — no pre-booking",
    badgeRecommended: "Recommended",
    badgeFreeCancellation: "Free Cancellation",
    badgeLowestPrice: "Lowest Price",
    standardPolicyStrip:
      "Semi-flex with continental breakfast (rescheduling subject to availability & rate)",
  },
  ratePlans: {
    standard: {
      name: "Standard Daily Rate",
      included: [
        "Continental Breakfast Included",
        "High Speed Wi‑Fi",
        "Flexible to Reschedule (Subject to Availability and Rate)",
        "10% Off Resort Amenities Booked Online",
      ],
    },
    signature: {
      name: "BRS Signature Privileges",
      breakfastWifiLine: "All Standard Benefits",
      included: [
        "15% Off All Resort Amenities During Your Stay",
        "Welcome Drink & Fruit Basket Included",
      ],
      thermalPackageWorthTemplate: "Complimentary Thermal Wellness Package",
      thermalPackageFacilitiesLine: "Sauna | Ice Plunge | Steam Bath",
    },
  },
  filters: {
    All: "All",
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau View",
    "Lake View": "Lake View",
    "Beatenberg View": "Beatenberg View",
    Balcony: "Balcony",
    "No balcony": "No balcony",
    Suites: "Suites",
  },
  viewBadges: {
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau View",
    "Lake View": "Lake View",
    "Beatenberg View": "Beatenberg View",
    "Village Side": "Village Side",
    "Village View": "Village View",
    "Standard View": "Standard View",
  },
  addonFilters: {
    All: "All",
    "Spa & Massage": "Spa & Massage",
    Dining: "Dining",
    Romance: "Romance",
  },
  roomCard: {
    balconyBadge: "Balcony",
    privateBalcony: "Private Balcony",
    alpineComfort: "Alpine Comfort",
    elegantSeating: "Elegant Seating",
    ensuiteCoffee: "Ensuite Coffee Machine",
    showRatesAndOffers: "Show rates & offers",
    hideRatesAndOffers: "Hide rates & offers",
    notAvailableDates: "Not available for your dates",
    from: "From",
    perNight: "/ night",
    perNightSlash: "/night",
    beddingLabels: {
      "1 Single Bed": "1 Single Bed",
      "1 Double Bed": "1 Double Bed",
      "2 Single Beds": "2 Single Beds",
      "1 Double + 1 Single Bed or 3 Single Beds":
        "1 Double + 1 Single or 3 Singles",
      "1 King Bed": "1 King Bed",
      "1 Double Bed or 2 Twin Beds": "1 Double or 2 Twin Beds",
      "3 Double Beds + Sofa Bed": "3 Doubles + Sofa Bed",
    },
    familySuiteBedding: "2 Queen + 4 Twin",
  },
  rateCard: {
    pricingNotAvailable: "Pricing not available for these dates",
    selectDatesForPricing: "Select different dates or room to see pricing",
    totalForStay: "Total for your",
    nightStayOne: "1-night stay",
    nightStayMany: "{{n}}-night stay",
    youSaved: "- Save {{amount}}",
    perNightLabel: "Per night:",
    previously: "Previously",
    previouslyParen: "previously",
    selected: "Selected",
    selectRate: "Select rate",
  },
  validation: {
    firstNameRequired: "First name is required.",
    lastNameRequired: "Last name is required.",
    emailRequired: "Email is required.",
    emailInvalid: "Please enter a valid email address.",
    telephoneRequired: "Phone number is required.",
    telephoneTooShort: "Phone number is too short for the selected country.",
    telephoneTooLong: "Phone number is too long for the selected country.",
    telephoneInvalidLength: "Phone number has an invalid length for the selected country.",
    telephoneLengthInvalid: "Phone number length is invalid for the selected country.",
    telephoneInvalid: "Please enter a valid phone number.",
    bookerFirstNameRequired: "Booker first name is required.",
    bookerLastNameRequired: "Booker last name is required.",
    bookerEmailRequired: "Booker email is required.",
    bookerEmailInvalid: "Please enter a valid booker email address.",
    bookerTelephoneRequired: "Booker phone number is required.",
    completeGuestDetails: "Please complete all required guest details.",
    completePaymentFields: "Please complete all payment fields correctly.",
    expirationRequired: "Expiration is required.",
    expirationFormat: "Use format MM/YY (e.g. 12/30).",
    expirationValid: "Please enter a valid expiration date.",
    monthRange: "Month must be 01–12.",
    cardExpired: "Card must not be expired.",
    holderNameRequired: "Cardholder name is required.",
  },
  errors: {
    loadReservation:
      "We couldn’t load your reservation. Please contact us with your confirmation number.",
    loadAvailabilityConfig:
      "We couldn’t load live availability yet. Please try again in a moment or contact us directly.",
    loadAvailabilityAge:
      "Live availability is temporarily unavailable. Please contact us directly to book these dates.",
    occupancyInvalid:
      "We couldn’t determine a valid occupancy for this selection. Please adjust guests and try again.",
    retrieveAvailability:
      "We couldn’t retrieve live availability right now. Please try again or contact us directly.",
    availabilityChanged:
      "Availability has changed; one or more selected options are no longer available. Please go back and choose your room and dates again.",
    createReservation: "We couldn’t create your reservation. Please try again or contact us.",
    paymentNotLinked: "Payment could not be linked. Please contact us.",
    configError: "Configuration error. Please contact us.",
    verifyCard: "We couldn't verify your card. Please try again or contact us.",
    loadReservationShort:
      "We couldn't load your reservation. Please contact us with your confirmation number.",
    cardNotAuthorized: "This card cannot be authorized. Please try another card or contact us.",
    cardDeclined: "Your card was declined. Please try another card or contact us.",
    authCanceled: "Authorization was canceled. Please try again or contact us.",
    cardVerificationFailed: "Card verification failed. Please check your card details and try again.",
    cardVerificationFailedGeneric: "Card verification failed. Please try again.",
    roomMappingMissing:
      "Room mapping is missing. Please go back and confirm your dates and room selection.",
    finalPriceProceed: " You can still proceed; the amount will be confirmed at checkout.",
    verifyFinalPrice: "Could not verify final price.",
    reservationPriceAlignment:
      "The pricing service returned an unexpected answer for your rooms. Please go back, review each room, and try again.",
  },
  policies: {
    charges: "Charges",
    ratesPerNight: "Rates (per night):",
    nights: "nights",
    night: "night",
    selectRoomRate: "Select a room and rate plan to see exact charges.",
    roomTotal: "Room total",
    addOns: "Add-ons",
    total: "TOTAL",
    includingAllTaxes: "INCLUDING ALL TAXES",
    guaranteeTitle: "Guarantee policy",
    guaranteeBody: "Placeholder. Deposit and payment rules will be finalized during integration.",
    cancellationTitle: "Cancellation policy",
    brsSignaturePrefix: "BRS Signature: ",
    checkInTitle: "Check-in policy",
    checkInBody:
      "Placeholder. Check-in after 15:00. Check-out before 11:00 (Signature: late checkout 12:00).",
    childrenTitle: "Children policy",
    childrenBody: "Placeholder. Children policy details.",
    petsTitle: "Pet policy",
    petsBody: "Placeholder. Pet policy details.",
    includingTaxesFees: "Including taxes & fees",
  },
  bar: {
    arrivalLong: "Arrival:",
    arrivalShort: "Arrive",
    departureLong: "Departure:",
    departureShort: "Depart",
    selectDate: "Select date",
    guest: "Guest",
    guests: "Guests",
    room: "Room",
    rooms: "Rooms",
    checkAvailability: "Check availability & update prices",
    organicBedding: "Organic bio cotton bedding in every room",
    ensuiteBath: "Ensuite bathroom in all rooms",
    adults: "Adults",
    children: "Children",
    guestCounterAdultSingular: "Adult",
    guestCounterAdultPlural: "Adults",
    guestCounterChildSingular: "Child",
    guestCounterChildPlural: "Children",
    guestCounterChildAgeHint: "(3 - 12 yrs)",
    roomLabelTemplate: "Stay {{n}}",
    addRoom: "+ Add room",
    removeRoom: "Remove",
    maxRoomsTitle: "Need 6 rooms or more?",
    maxRoomsContactHtml:
      'Contact: <a href="mailto:info@beatenberg-resortspa.ch" class="text-pine-green font-medium underline underline-offset-2">info@beatenberg-resortspa.ch</a>',
  },
  leaveGuard: {
    warning:
      "You are leaving this page. If you proceed, your selection will be cleared.",
    question: "Would you like to proceed?",
    stay: "No",
    leave: "Yes",
  },
  criteriaOverlay: {
    title: "Select dates & guests",
    dates: "Dates",
    guests: "Guests",
  },
  splitOccupancy: {
    dividedAcrossRoomsToast:
      "Your stay was divided across {{n}} rooms to match room types.",
    guidanceUseAddRoomToast:
      "More than two guests in one room? Use {{addRoom}} to book an extra room.",
    splitBlockedMaxRooms:
      "That guest count needs more than five rooms. Please contact us or reduce the number of guests per room.",
    splitBlockedInsufficientAdults:
      "Each room needs at least one adult. Add adults or use {{addRoom}} to split guests across rooms.",
    barApplyRequiresOverlayFirst:
      "Please confirm dates and guests using the panel in the center of the page first.",
    splitOverflowInlineLead:
      "Your party would need more than five rooms. Reduce guests per row, add rows with fewer guests, or contact us.",
    splitInsufficientAdultsTitle: "Adults required per room",
    barSplitGuestHintCompact:
      "Split your party across more rooms for better inventory and room choices.",
  },
  confirmation: {
    backHome: "Back to home",
    title: "Booking confirmed",
    subtitle: "Thank you for your reservation.",
    loadingTitle: "Loading your reservation",
    loadingHint: "One moment, please.",
    contactWithNumber: "Please contact us with your confirmation number if you have it.",
    confirmedTitle: "Your reservation is confirmed",
    confirmedBodyPrefix: "Thank you for choosing us. Confirmation number",
    confirmedBodySuffix:
      ". A confirmation email with payment and stay details will follow shortly.",
    numbersPrefix: "s:",
    stayDetails: "Stay details",
    rooms: "Rooms",
    datesNights: "Dates & nights",
    guestsLabel: "Guests",
    adult: "adult",
    adults: "adults",
    child: "child",
    children: "children",
    additionalServices: "Additional services",
    servicesIncluded: "Included in your reservation",
    total: "Total",
    reservationCreated: "Reservation created",
    reservationCreatedBodyPrefix: "Your reservation has been confirmed. Confirmation number",
    reservationCreatedBodySuffix: ". Payment and confirmation details will follow in the next steps.",
    cardNotAvailableTitle: "Card payment not available",
    cardNotAvailableBody:
      "Secure card payment is not available for this property at the moment. Please contact us to complete your reservation.",
    contactUs: "Contact us",
    verifyingPrice: "Verifying final price…",
    priceChanged: "The final price has changed from the quoted amount. Please review below.",
    finalPrice: "Final price",
    amountChargeOnConfirmation: "Amount to charge on confirmation:",
    paymentDetails: "Payment details",
    paymentSecureNote: "Card number and CVC are secured by our payment provider.",
    nameOnCard: "Name on Card",
    validity: "Validity",
    tryAnotherCardHint: "You can try another card below. Your guest details are saved.",
    tryAnotherCard: "Try another card",
    processing: "Processing…",
    confirmBooking: "Confirm booking",
    roomFallbackName: "Room",
    confirmationNumberShort: "No. {{number}}",
    unknownProduct: "Additional service",
  },
  steps: {
    chooseViewTitle: "Plan your stay",
    chooseViewTitleWithProgress: "Plan your stay ({{current}}/{{total}})",
    chooseViewActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    stayStepRoomProgress: "Room {{current}} of {{total}}",
    chooseViewSubtitle: "Select your preferred room and view.",
    noRoomsMatch:
      "No rooms match this occupancy. Try “Show all rooms” or reduce guest count.",
    enhanceTitle: "Enhance your stay",
    enhanceTitleWithProgress: "Enhance your stay ({{current}}/{{total}})",
    enhanceSubtitle: "Tailor your experience with exclusive add-ons.",
    enhanceActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    editStayRoomOccupancyAria: "Change guest count for {{label}}",
    editStayRoomOccupancyTitle: "Guests — {{label}}",
    noAddonsCategory: "No add-ons in this category.",
    detailsTitle: "Guest details & payment",
    detailsSubtitle: "Enter your details and secure your reservation.",
    bookingForSelf: "I'm booking for myself",
    bookingForOther: "I'm booking for someone else",
    processingReservation: "Processing your reservation",
    doNotClose: "Securing your stay — please don't close this page.",
    yourDetails: "Your details",
    guestDetailsSection: "Guest details",
    firstName: "First name *",
    lastName: "Last name *",
    email: "Email *",
    emailHint: "We'll send a confirmation email to this address.",
    phone: "Phone",
    nationality: "Nationality",
    specialRequests: "Special requests",
    marketingOptIn: "I’d like to receive offers and news by email",
    marketingOptInBooker: "I’d like to receive offers and news by email (booker)",
    backAria: "Back",
  },
  summary: {
    title: "Stay Summary",
    chooseRoom: "Choose a room",
    chooseRate: "Choose a rate",
    edit: "Edit",
    stayEnhancements: "Stay Enhancements",
    enhancementDiscountNoRate: "10-15% Off all Resort Amenities",
    enhancementDiscountSignature: "15% Off all Resort Amenities during your stay.",
    enhancementDiscountStandard: "10% Off all Resort Amenities Booked Online",
    noAddons: "No add-ons selected.",
    exclusiveBenefits: "Exclusive Benefits",
    benefit1Title: "Exclusive offers & inventory for direct bookings",
    benefit1Desc: "The best prices and availability when you book with us directly",
    benefit2Title: "Continental Breakfast Included",
    benefit2Desc: "Start each morning with our complimentary breakfast",
    benefit3Title: "Best price guarantee",
    benefit3Desc: "Book direct for our lowest rates and exclusive packages",
    continue: "Continue",
    back: "Back",
    selectRoomRateContinue: "Select a room and rate plan to continue.",
    selected: "Selected",
    selectRatePlan: "Select a rate plan",
    closeExpandedSummaryAria: "Close stay summary",
    multiRoomPlanStickyHeading: "Your rooms",
    roomPlanNotSelected: "Not selected",
    mobileMultiRoomStayProgress: "Stay ({{k}}/{{n}})",
    mobileCollapsedDetailsTotalNightsLine: "Total : {{count}} {{nightsLabel}}",
    mobileFreeCancellationPillTillDate: "till {{date}}",
    expandedSheetCheckoutLink: "Checkout",
    toggleMultiRoomAddonsAria: "Show or hide add-ons for this stay",
  },
  bestRateGuarantee: {
    headline: "Best Rate Guaranteed",
    subline: "When you book direct with us.",
    learnMore: "Learn more",
  },
  carousel: {
    goToImage: "Go to image",
    viewFullScreen: "View photos full screen",
    photosTitle: "photos",
    photosDescription: "Swipe through room photos.",
  },
  roomLadder: bookRoomLadderCopyEn,
  upsells: bookUpsellsCopyEn,
  roomTags: bookRoomTagsEn,
  upsellCategoryLabels: {
    Dining: "Dining",
    Romance: "Romance",
    "Spa & Massage": "Spa & Massage",
    "Sauna & Thermal": "Sauna & Thermal",
  },
  upsellCard: {
    decreaseAria: "Decrease quantity",
    increaseAria: "Increase quantity",
    add: "Add to stay",
    added: "Added",
    pricedForRoomGuests: "Priced for this room's guests.",
  },
};

export const bookPageTranslationsDe: BookPageTranslations = {
  directBookerDiscountLabel: "Direktbucher-Rabatt",
  signature: {
    cancelTimeLabel: "0:00 Uhr MEZ",
    cancelTimeStrip: "0:00 Uhr",
    pastDeadlinePerkLine:
      "10 % Rabatt auf Spa- und Resort-Extras während Ihres Aufenthalts — keine Vorausbuchung nötig.",
    bannerSelectDates:
      "Wählen Sie Ihre Aufenthaltsdaten für flexible Stornierungsdetails (0:00 Uhr).",
    bannerFullyFlexibleUntil: "Vollständig flexible Stornierung bis {{date}}, {{time}}",
    summaryFreeUntil: "Kostenlose Stornierung bis {{date}}, {{time}}",
    summarySelectDates:
      "Wählen Sie das Check-in-Datum, um die Frist für kostenlose Stornierung (0:00 Uhr MEZ) zu sehen.",
    summaryChooseSignature:
      "Stornierungsbedingungen hängen vom gewählten Tarif ab. Wählen Sie Signature für Details zur kostenlosen Stornierung.",
    taglineMobileNoDates: "Kostenlose Stornierung — Daten wählen",
    taglineMobileUntil: "Kostenlose Stornierung bis {{date}}",
    taglineMobilePastDeadline: "10 % Spa- & Resort-Extras — ohne Vorbuchung",
    badgeRecommended: "Empfohlen",
    badgeFreeCancellation: "Kostenlose Stornierung",
    badgeLowestPrice: "Bester Preis",
    standardPolicyStrip:
      "Teilflexibel mit kontinentalem Frühstück (Umbuchung je nach Verfügbarkeit & Tarif)",
  },
  ratePlans: {
    standard: {
      name: "Standard-Tagestarif",
      included: [
        "Kontinentales Frühstück inbegriffen",
        "Highspeed-WLAN",
        "Flexibel umbuchbar (je nach Verfügbarkeit und Tarif)",
        "10 % Rabatt auf Resort-Annehmlichkeiten bei Online-Buchung",
      ],
    },
    signature: {
      name: "BRS Signature Privileges",
      breakfastWifiLine: "Alle Standard-Leistungen",
      included: [
        "15 % Rabatt auf alle Resort-Annehmlichkeiten während Ihres Aufenthalts",
        "Willkommensgetränk & Obstkorb inbegriffen",
      ],
      thermalPackageWorthTemplate: "Komplimentäres Thermal-Wellness-Paket",
      thermalPackageFacilitiesLine: "Sauna | Eistauchbecken | Dampfbad",
    },
  },
  filters: {
    All: "Alle",
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau",
    "Lake View": "Seeblick",
    "Beatenberg View": "Beatenberg",
    Balcony: "Balkon",
    "No balcony": "Ohne Balkon",
    Suites: "Suiten",
  },
  viewBadges: {
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau",
    "Lake View": "Seeblick",
    "Beatenberg View": "Beatenberg",
    "Village Side": "Dorfseite",
    "Village View": "Dorf",
    "Standard View": "Standardaussicht",
  },
  addonFilters: {
    All: "Alle",
    "Spa & Massage": "Spa & Massage",
    Dining: "Gastronomie",
    Romance: "Romantik",
  },
  roomCard: {
    balconyBadge: "Balkon",
    privateBalcony: "Privater Balkon",
    alpineComfort: "Alpiner Komfort",
    elegantSeating: "Elegante Sitzgelegenheit",
    ensuiteCoffee: "Kaffeemaschine im Zimmer",
    showRatesAndOffers: "Tarife & Angebote einblenden",
    hideRatesAndOffers: "Tarife & Angebote ausblenden",
    notAvailableDates: "Für Ihre Daten nicht verfügbar",
    from: "Ab",
    perNight: "/ Nacht",
    perNightSlash: "/Nacht",
    beddingLabels: {
      "1 Single Bed": "1 Einzelbett",
      "1 Double Bed": "1 Doppelbett",
      "2 Single Beds": "2 Einzelbetten",
      "1 Double + 1 Single Bed or 3 Single Beds":
        "1 Doppel- + 1 Einzelbett oder 3 Einzelbetten",
      "1 King Bed": "1 Kingsize-Bett",
      "1 Double Bed or 2 Twin Beds": "1 Doppelbett oder 2 Einzelbetten",
      "3 Double Beds + Sofa Bed": "3 Doppelbetten + Schlafsofa",
    },
    familySuiteBedding: "2 Queen-Betten + 4 Twin-Betten",
  },
  rateCard: {
    pricingNotAvailable: "Für diese Daten kein Preis verfügbar",
    selectDatesForPricing: "Andere Daten oder ein anderes Zimmer wählen für Preise",
    totalForStay: "Gesamt für Ihren",
    nightStayOne: "1 Übernachtung",
    nightStayMany: "{{n}} Übernachtungen",
    youSaved: "- {{amount}} sparen",
    perNightLabel: "Pro Nacht:",
    previously: "Zuvor",
    previouslyParen: "zuvor",
    selected: "Ausgewählt",
    selectRate: "Tarif wählen",
  },
  validation: {
    firstNameRequired: "Vorname ist erforderlich.",
    lastNameRequired: "Nachname ist erforderlich.",
    emailRequired: "E-Mail ist erforderlich.",
    emailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    telephoneRequired: "Telefonnummer ist erforderlich.",
    telephoneTooShort: "Telefonnummer ist für das gewählte Land zu kurz.",
    telephoneTooLong: "Telefonnummer ist für das gewählte Land zu lang.",
    telephoneInvalidLength: "Telefonnummer hat für das gewählte Land eine ungültige Länge.",
    telephoneLengthInvalid: "Telefonnummer hat für das gewählte Land eine ungültige Länge.",
    telephoneInvalid: "Bitte geben Sie eine gültige Telefonnummer ein.",
    bookerFirstNameRequired: "Vorname des Buchenden ist erforderlich.",
    bookerLastNameRequired: "Nachname des Buchenden ist erforderlich.",
    bookerEmailRequired: "E-Mail des Buchenden ist erforderlich.",
    bookerEmailInvalid: "Bitte geben Sie eine gültige E-Mail des Buchenden ein.",
    bookerTelephoneRequired: "Telefonnummer des Buchenden ist erforderlich.",
    completeGuestDetails: "Bitte füllen Sie alle erforderlichen Gastdaten aus.",
    completePaymentFields: "Bitte füllen Sie alle Zahlungsfelder korrekt aus.",
    expirationRequired: "Gültigkeit ist erforderlich.",
    expirationFormat: "Format MM/JJ verwenden (z. B. 12/30).",
    expirationValid: "Bitte geben Sie ein gültiges Ablaufdatum ein.",
    monthRange: "Monat muss 01–12 sein.",
    cardExpired: "Karte darf nicht abgelaufen sein.",
    holderNameRequired: "Name des Karteninhabers ist erforderlich.",
  },
  errors: {
    loadReservation:
      "Ihre Reservierung konnte nicht geladen werden. Bitte kontaktieren Sie uns mit Ihrer Bestätigungsnummer.",
    loadAvailabilityConfig:
      "Live-Verfügbarkeit konnte noch nicht geladen werden. Bitte versuchen Sie es in Kürze erneut oder kontaktieren Sie uns.",
    loadAvailabilityAge:
      "Live-Verfügbarkeit ist vorübergehend nicht verfügbar. Bitte kontaktieren Sie uns direkt für diese Daten.",
    occupancyInvalid:
      "Für diese Auswahl konnte keine gültige Belegung ermittelt werden. Bitte passen Sie die Gästezahl an.",
    retrieveAvailability:
      "Live-Verfügbarkeit konnte gerade nicht abgerufen werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.",
    availabilityChanged:
      "Die Verfügbarkeit hat sich geändert; eine oder mehrere Optionen sind nicht mehr verfügbar. Bitte wählen Sie Zimmer und Daten erneut.",
    createReservation: "Reservierung konnte nicht erstellt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.",
    paymentNotLinked: "Zahlung konnte nicht verknüpft werden. Bitte kontaktieren Sie uns.",
    configError: "Konfigurationsfehler. Bitte kontaktieren Sie uns.",
    verifyCard: "Karte konnte nicht verifiziert werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.",
    loadReservationShort:
      "Reservierung konnte nicht geladen werden. Bitte kontaktieren Sie uns mit Ihrer Bestätigungsnummer.",
    cardNotAuthorized: "Diese Karte kann nicht autorisiert werden. Bitte andere Karte oder Kontakt.",
    cardDeclined: "Karte wurde abgelehnt. Bitte andere Karte oder Kontakt.",
    authCanceled: "Autorisierung wurde abgebrochen. Bitte erneut versuchen oder kontaktieren.",
    cardVerificationFailed: "Kartenprüfung fehlgeschlagen. Bitte Daten prüfen und erneut versuchen.",
    cardVerificationFailedGeneric: "Kartenprüfung fehlgeschlagen. Bitte erneut versuchen.",
    roomMappingMissing:
      "Zimmerzuordnung fehlt. Bitte gehen Sie zurück und bestätigen Sie Daten und Zimmerwahl.",
    finalPriceProceed:
      " Sie können fortfahren; der Betrag wird beim Checkout bestätigt.",
    verifyFinalPrice: "Endpreis konnte nicht geprüft werden.",
    reservationPriceAlignment:
      "Der Preisdienst hat eine unerwartete Antwort für Ihre Zimmer geliefert. Bitte gehen Sie zurück, prüfen Sie jedes Zimmer und versuchen Sie es erneut.",
  },
  policies: {
    charges: "Kosten",
    ratesPerNight: "Tarife (pro Nacht):",
    nights: "Nächte",
    night: "Nacht",
    selectRoomRate: "Zimmer und Tarif wählen für genaue Kosten.",
    roomTotal: "Zimmer gesamt",
    addOns: "Zusatzleistungen",
    total: "TOTAL",
    includingAllTaxes: "INKL. ALLER STEUERN",
    guaranteeTitle: "Garantiebedingungen",
    guaranteeBody: "Platzhalter. Anzahlung und Zahlungsregeln werden bei der Integration finalisiert.",
    cancellationTitle: "Stornierungsbedingungen",
    brsSignaturePrefix: "BRS Signature: ",
    checkInTitle: "Check-in",
    checkInBody:
      "Platzhalter. Check-in ab 15:00. Check-out bis 11:00 (Signature: späterer Check-out 12:00).",
    childrenTitle: "Kinder",
    childrenBody: "Platzhalter. Details zur Kinderpolicy.",
    petsTitle: "Haustiere",
    petsBody: "Platzhalter. Details zur Haustierpolicy.",
    includingTaxesFees: "Inkl. Steuern & Gebühren",
  },
  bar: {
    arrivalLong: "Anreise:",
    arrivalShort: "Anreise",
    departureLong: "Abreise:",
    departureShort: "Abreise",
    selectDate: "Datum wählen",
    guest: "Gast",
    guests: "Gäste",
    room: "Zimmer",
    rooms: "Zimmer",
    checkAvailability: "Verfügbarkeit prüfen & Preise aktualisieren",
    organicBedding: "Bio-Baumwollbettwäsche in jedem Zimmer",
    ensuiteBath: "Eigenes Bad in allen Zimmern",
    adults: "Erwachsene",
    children: "Kinder",
    guestCounterAdultSingular: "Erwachsener",
    guestCounterAdultPlural: "Erwachsene",
    guestCounterChildSingular: "Kind",
    guestCounterChildPlural: "Kinder",
    guestCounterChildAgeHint: "(3–12 Jahre)",
    roomLabelTemplate: "Aufenthalt {{n}}",
    addRoom: "+ Zimmer hinzufügen",
    removeRoom: "Entfernen",
    maxRoomsTitle: "6 Zimmer oder mehr?",
    maxRoomsContactHtml:
      'Kontakt: <a href="mailto:info@beatenberg-resortspa.ch" class="text-pine-green font-medium underline underline-offset-2">info@beatenberg-resortspa.ch</a>',
  },
  leaveGuard: {
    warning:
      "Sie verlassen diese Seite. Wenn Sie fortfahren, wird Ihre Auswahl verworfen.",
    question: "Möchten Sie fortfahren?",
    stay: "Nein",
    leave: "Ja",
  },
  criteriaOverlay: {
    title: "Datum & Gäste wählen",
    dates: "Daten",
    guests: "Gäste",
  },
  splitOccupancy: {
    dividedAcrossRoomsToast:
      "Ihr Aufenthalt wurde auf {{n}} Zimmer verteilt, passend zu den Zimmertypen.",
    guidanceUseAddRoomToast:
      "Mehr als zwei Gäste in einem Zimmer? Nutzen Sie {{addRoom}} für ein weiteres Zimmer.",
    splitBlockedMaxRooms:
      "Für diese Gästezahl bräuchten Sie mehr als fünf Zimmer. Bitte kontaktieren Sie uns oder reduzieren Sie die Gäste pro Zimmer.",
    splitBlockedInsufficientAdults:
      "Pro Zimmer ist mindestens ein Erwachsener nötig. Erhöhen Sie die Erwachsenenzahl oder nutzen Sie {{addRoom}}, um Gäste aufzuteilen.",
    barApplyRequiresOverlayFirst:
      "Bitte bestätigen Sie zuerst Datum und Gäste im Fenster in der Mitte der Seite.",
    splitOverflowInlineLead:
      "Für Ihre Auswahl würden mehr als fünf Zimmer nötig sein. Reduzieren Sie die Gäste pro Zeile, teilen Sie auf mehr Zeilen mit weniger Gästen auf, oder kontaktieren Sie uns.",
    splitInsufficientAdultsTitle: "Erwachsene pro Zimmer erforderlich",
    barSplitGuestHintCompact:
      "Verteilen Sie Ihre Gruppe auf mehrere Zimmer — so stehen Ihnen mehr Kategorien und Zimmer zur Verfügung.",
  },
  confirmation: {
    backHome: "Zurück zur Startseite",
    title: "Buchung bestätigt",
    subtitle: "Vielen Dank für Ihre Reservierung.",
    loadingTitle: "Reservierung wird geladen",
    loadingHint: "Einen Moment bitte.",
    contactWithNumber: "Bitte kontaktieren Sie uns mit Ihrer Bestätigungsnummer, falls vorhanden.",
    confirmedTitle: "Ihre Reservierung ist bestätigt",
    confirmedBodyPrefix: "Vielen Dank für Ihre Wahl. Bestätigungsnummer",
    confirmedBodySuffix:
      ". Eine Bestätigungs-E-Mail mit Zahlungs- und Aufenthaltsdetails folgt in Kürze.",
    numbersPrefix: "n:",
    stayDetails: "Aufenthaltsdetails",
    rooms: "Zimmer",
    datesNights: "Daten & Nächte",
    guestsLabel: "Gäste",
    adult: "Erwachsener",
    adults: "Erwachsene",
    child: "Kind",
    children: "Kinder",
    additionalServices: "Zusatzleistungen",
    servicesIncluded: "In Ihrer Reservierung enthalten",
    total: "Total",
    reservationCreated: "Reservierung erstellt",
    reservationCreatedBodyPrefix: "Ihre Reservierung ist bestätigt. Bestätigungsnummer",
    reservationCreatedBodySuffix: ". Zahlungs- und Bestätigungsdetails folgen in den nächsten Schritten.",
    cardNotAvailableTitle: "Kartenzahlung nicht verfügbar",
    cardNotAvailableBody:
      "Sichere Kartenzahlung ist derzeit für diese Unterkunft nicht verfügbar. Bitte kontaktieren Sie uns zur Fertigstellung.",
    contactUs: "Kontakt",
    verifyingPrice: "Endpreis wird geprüft…",
    priceChanged: "Der Endpreis hat sich gegenüber dem Angebot geändert. Bitte prüfen Sie unten.",
    finalPrice: "Endpreis",
    amountChargeOnConfirmation: "Bei Bestätigung zu belastender Betrag:",
    paymentDetails: "Zahlungsdaten",
    paymentSecureNote: "Kartennummer und CVC sind bei unserem Zahlungsanbieter geschützt.",
    nameOnCard: "Name auf der Karte",
    validity: "Gültigkeit",
    tryAnotherCardHint: "Sie können unten eine andere Karte versuchen. Ihre Gastdaten sind gespeichert.",
    tryAnotherCard: "Andere Karte versuchen",
    processing: "Wird bearbeitet…",
    confirmBooking: "Buchung bestätigen",
    roomFallbackName: "Zimmer",
    confirmationNumberShort: "Nr. {{number}}",
    unknownProduct: "Zusatzleistung",
  },
  steps: {
    chooseViewTitle: "Planen Sie Ihren Aufenthalt",
    chooseViewTitleWithProgress: "Aufenthalt planen ({{current}}/{{total}})",
    chooseViewActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    stayStepRoomProgress: "Zimmer {{current}} von {{total}}",
    chooseViewSubtitle: "Wählen Sie Ihr bevorzugtes Zimmer und die Aussicht.",
    noRoomsMatch:
      "Kein Zimmer passt zu dieser Belegung. „Alle Zimmer anzeigen“ wählen oder Gästezahl reduzieren.",
    enhanceTitle: "Aufenthalt verfeinern",
    enhanceTitleWithProgress: "Aufenthalt verfeinern ({{current}}/{{total}})",
    enhanceSubtitle: "Gestalten Sie Ihren Aufenthalt mit exklusiven Zusatzleistungen.",
    enhanceActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    editStayRoomOccupancyAria: "Gästezahl für {{label}} ändern",
    editStayRoomOccupancyTitle: "Gäste — {{label}}",
    noAddonsCategory: "Keine Add-ons in dieser Kategorie.",
    detailsTitle: "Gästedaten & Zahlung",
    detailsSubtitle: "Geben Sie Ihre Daten ein und sichern Sie Ihre Reservierung.",
    bookingForSelf: "Ich buche für mich selbst",
    bookingForOther: "Ich buche für eine andere Person",
    processingReservation: "Reservierung wird bearbeitet",
    doNotClose: "Ihr Aufenthalt wird gesichert — bitte diese Seite nicht schließen.",
    yourDetails: "Ihre Daten",
    guestDetailsSection: "Gästedaten",
    firstName: "Vorname *",
    lastName: "Nachname *",
    email: "E-Mail *",
    emailHint: "Wir senden eine Bestätigung an diese Adresse.",
    phone: "Telefon",
    nationality: "Nationalität",
    specialRequests: "Besondere Wünsche",
    marketingOptIn: "Ich möchte Angebote und Neuigkeiten per E-Mail erhalten",
    marketingOptInBooker: "Ich möchte Angebote und Neuigkeiten per E-Mail (Buchende/r)",
    backAria: "Zurück",
  },
  summary: {
    title: "Aufenthaltsübersicht",
    chooseRoom: "Zimmer wählen",
    chooseRate: "Tarif wählen",
    edit: "Bearbeiten",
    stayEnhancements: "Aufenthalts-Extras",
    enhancementDiscountNoRate: "10–15 % Rabatt auf alle Resort-Annehmlichkeiten",
    enhancementDiscountSignature:
      "15 % Rabatt auf alle Resort-Annehmlichkeiten während Ihres Aufenthalts.",
    enhancementDiscountStandard: "10 % Rabatt auf alle Resort-Annehmlichkeiten bei Online-Buchung",
    noAddons: "Keine Add-ons ausgewählt.",
    exclusiveBenefits: "Exklusive Vorteile",
    benefit1Title: "Exklusive Angebote & Kontingente für Direktbucher",
    benefit1Desc: "Beste Preise und Verfügbarkeit bei Direktbuchung",
    benefit2Title: "Kontinentales Frühstück inbegriffen",
    benefit2Desc: "Starten Sie jeden Morgen mit unserem Frühstück",
    benefit3Title: "Bestpreisgarantie",
    benefit3Desc: "Direkt buchen für unsere besten Tarife und Pakete",
    continue: "Weiter",
    back: "Zurück",
    selectRoomRateContinue: "Zimmer und Tarif wählen, um fortzufahren.",
    selected: "Ausgewählt",
    selectRatePlan: "Tarif wählen",
    closeExpandedSummaryAria: "Aufenthaltsübersicht schließen",
    multiRoomPlanStickyHeading: "Ihre Zimmer",
    roomPlanNotSelected: "Nicht gewählt",
    mobileMultiRoomStayProgress: "Aufenthalt ({{k}}/{{n}})",
    mobileCollapsedDetailsTotalNightsLine: "Gesamt : {{count}} {{nightsLabel}}",
    mobileFreeCancellationPillTillDate: "bis {{date}}",
    expandedSheetCheckoutLink: "Zur Kasse",
    toggleMultiRoomAddonsAria: "Zusatzleistungen für diesen Aufenthalt ein- oder ausblenden",
  },
  bestRateGuarantee: {
    headline: "Bestpreisgarantie",
    subline: "Wenn Sie direkt bei uns buchen.",
    learnMore: "Mehr erfahren",
  },
  carousel: {
    goToImage: "Zu Bild",
    viewFullScreen: "Fotos Vollbild",
    photosTitle: "Fotos",
    photosDescription: "Wischen Sie durch die Zimmerfotos.",
  },
  roomLadder: bookRoomLadderCopyDe,
  upsells: bookUpsellsCopyDe,
  roomTags: bookRoomTagsDe,
  upsellCategoryLabels: {
    Dining: "Gastronomie",
    Romance: "Romantik",
    "Spa & Massage": "Spa & Massage",
    "Sauna & Thermal": "Sauna & Therme",
  },
  upsellCard: {
    decreaseAria: "Menge verringern",
    increaseAria: "Menge erhöhen",
    add: "Zum Aufenthalt hinzufügen",
    added: "Hinzugefügt",
    pricedForRoomGuests: "Preis gilt für die Gäste dieses Zimmers.",
  },
};

export const bookPageTranslationsFr: BookPageTranslations = {
  directBookerDiscountLabel: "Réduction réservation directe",
  signature: {
    cancelTimeLabel: "minuit CET",
    cancelTimeStrip: "minuit",
    pastDeadlinePerkLine:
      "10 % sur les extras spa et resort pendant votre séjour — sans réservation préalable.",
    bannerSelectDates:
      "Choisissez vos dates de séjour pour voir les conditions d’annulation flexibles (minuit).",
    bannerFullyFlexibleUntil: "Annulation entièrement flexible jusqu’au {{date}}, {{time}}",
    summaryFreeUntil: "Annulation gratuite jusqu’au {{date}}, {{time}}",
    summarySelectDates:
      "Choisissez les dates d’arrivée pour voir la date limite d’annulation gratuite (minuit CET).",
    summaryChooseSignature:
      "Les conditions d’annulation dépendent du tarif choisi. Choisissez Signature pour les détails d’annulation gratuite.",
    taglineMobileNoDates: "Annulation gratuite — choisissez les dates",
    taglineMobileUntil: "Annulation gratuite jusqu’au {{date}}",
    taglineMobilePastDeadline: "10 % spa & extras — sans réservation",
    badgeRecommended: "Recommandé",
    badgeFreeCancellation: "Annulation gratuite",
    badgeLowestPrice: "Meilleur prix",
    standardPolicyStrip:
      "Semi-flexible avec petit-déjeuner continental (report selon disponibilité et tarif)",
  },
  ratePlans: {
    standard: {
      name: "Tarif journalier standard",
      included: [
        "Petit-Déjeuner Continental Inclus",
        "Wi‑Fi Haut Débit",
        "Report Possible (Selon Disponibilité et Tarif)",
        "10 % sur les Prestations du Resort Réservées en Ligne",
      ],
    },
    signature: {
      name: "Privilèges BRS Signature",
      breakfastWifiLine: "Tous les avantages du tarif Standard",
      included: [
        "15 % sur Toutes les Prestations du Resort Pendant Votre Séjour",
        "Boisson de Bienvenue & Corbeille de Fruits Incluses",
      ],
      thermalPackageWorthTemplate: "Forfait Bien-Être Thermal Offert",
      thermalPackageFacilitiesLine: "Sauna | Bain froid | Bain de vapeur",
    },
  },
  filters: {
    All: "Tous",
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau",
    "Lake View": "Vue lac",
    "Beatenberg View": "Beatenberg",
    Balcony: "Balcon",
    "No balcony": "Sans balcon",
    Suites: "Suites",
  },
  viewBadges: {
    "Eiger, Mönch & Jungfrau View": "Eiger, Mönch & Jungfrau",
    "Lake View": "Vue lac",
    "Beatenberg View": "Beatenberg",
    "Village Side": "Côté village",
    "Village View": "Vue village",
    "Standard View": "Vue standard",
  },
  addonFilters: {
    All: "Tous",
    "Spa & Massage": "Spa & massage",
    Dining: "Restauration",
    Romance: "Romantique",
  },
  roomCard: {
    balconyBadge: "Balcon",
    privateBalcony: "Balcon privé",
    alpineComfort: "Confort alpin",
    elegantSeating: "Coin salon élégant",
    ensuiteCoffee: "Machine à café dans la chambre",
    showRatesAndOffers: "Afficher tarifs & offres",
    hideRatesAndOffers: "Masquer tarifs & offres",
    notAvailableDates: "Non disponible pour vos dates",
    from: "À partir de",
    perNight: "/ nuit",
    perNightSlash: "/nuit",
    beddingLabels: {
      "1 Single Bed": "1 lit simple",
      "1 Double Bed": "1 lit double",
      "2 Single Beds": "2 lits simples",
      "1 Double + 1 Single Bed or 3 Single Beds":
        "1 double + 1 simple ou 3 simples",
      "1 King Bed": "1 lit king size",
      "1 Double Bed or 2 Twin Beds": "1 double ou 2 lits jumeaux",
      "3 Double Beds + Sofa Bed": "3 doubles + canapé-lit",
    },
    familySuiteBedding: "2 Queen + 4 lits jumeaux",
  },
  rateCard: {
    pricingNotAvailable: "Tarif non disponible pour ces dates",
    selectDatesForPricing: "Autres dates ou autre chambre pour voir les tarifs",
    totalForStay: "Total pour votre",
    nightStayOne: "1 nuit",
    nightStayMany: "{{n}} nuits",
    youSaved: "- Économisez {{amount}}",
    perNightLabel: "Par nuit :",
    previously: "Auparavant",
    previouslyParen: "auparavant",
    selected: "Sélectionné",
    selectRate: "Choisir le tarif",
  },
  validation: {
    firstNameRequired: "Le prénom est obligatoire.",
    lastNameRequired: "Le nom est obligatoire.",
    emailRequired: "L’e-mail est obligatoire.",
    emailInvalid: "Veuillez saisir une adresse e-mail valide.",
    telephoneRequired: "Le numéro de téléphone est obligatoire.",
    telephoneTooShort: "Numéro trop court pour le pays sélectionné.",
    telephoneTooLong: "Numéro trop long pour le pays sélectionné.",
    telephoneInvalidLength: "Longueur de numéro invalide pour le pays sélectionné.",
    telephoneLengthInvalid: "Longueur de numéro invalide pour le pays sélectionné.",
    telephoneInvalid: "Veuillez saisir un numéro de téléphone valide.",
    bookerFirstNameRequired: "Le prénom du réservant est obligatoire.",
    bookerLastNameRequired: "Le nom du réservant est obligatoire.",
    bookerEmailRequired: "L’e-mail du réservant est obligatoire.",
    bookerEmailInvalid: "Veuillez saisir une adresse e-mail valide pour le réservant.",
    bookerTelephoneRequired: "Le téléphone du réservant est obligatoire.",
    completeGuestDetails: "Veuillez compléter toutes les informations requises.",
    completePaymentFields: "Veuillez remplir correctement tous les champs de paiement.",
    expirationRequired: "La date d’expiration est obligatoire.",
    expirationFormat: "Format MM/AA (ex. 12/30).",
    expirationValid: "Veuillez saisir une date d’expiration valide.",
    monthRange: "Le mois doit être entre 01 et 12.",
    cardExpired: "La carte ne doit pas être expirée.",
    holderNameRequired: "Le nom du titulaire est obligatoire.",
  },
  errors: {
    loadReservation:
      "Impossible de charger votre réservation. Contactez-nous avec votre numéro de confirmation.",
    loadAvailabilityConfig:
      "La disponibilité en direct n’a pas pu être chargée. Réessayez dans un instant ou contactez-nous.",
    loadAvailabilityAge:
      "Disponibilité temporairement indisponible. Contactez-nous directement pour ces dates.",
    occupancyInvalid:
      "Occupation invalide pour cette sélection. Ajustez le nombre de personnes.",
    retrieveAvailability:
      "Impossible d’obtenir la disponibilité. Réessayez ou contactez-nous.",
    availabilityChanged:
      "La disponibilité a changé ; certaines options ne sont plus disponibles. Revenez en arrière pour choisir chambre et dates.",
    createReservation: "Impossible de créer la réservation. Réessayez ou contactez-nous.",
    paymentNotLinked: "Le paiement n’a pas pu être lié. Contactez-nous.",
    configError: "Erreur de configuration. Contactez-nous.",
    verifyCard: "Impossible de vérifier la carte. Réessayez ou contactez-nous.",
    loadReservationShort:
      "Impossible de charger la réservation. Contactez-nous avec votre numéro de confirmation.",
    cardNotAuthorized: "Cette carte ne peut pas être autorisée. Autre carte ou contactez-nous.",
    cardDeclined: "Carte refusée. Autre carte ou contactez-nous.",
    authCanceled: "Autorisation annulée. Réessayez ou contactez-nous.",
    cardVerificationFailed: "Échec de vérification. Vérifiez les données de la carte.",
    cardVerificationFailedGeneric: "Échec de vérification. Réessayez.",
    roomMappingMissing:
      "Correspondance chambre manquante. Retournez en arrière et confirmez dates et chambre.",
    finalPriceProceed:
      " Vous pouvez continuer ; le montant sera confirmé au paiement.",
    verifyFinalPrice: "Impossible de vérifier le prix final.",
    reservationPriceAlignment:
      "Le service de tarification a renvoyé une réponse inattendue pour vos chambres. Revenez en arrière, vérifiez chaque chambre et réessayez.",
  },
  policies: {
    charges: "Montants",
    ratesPerNight: "Tarifs (par nuit) :",
    nights: "nuits",
    night: "nuit",
    selectRoomRate: "Choisissez une chambre et un tarif pour voir le détail.",
    roomTotal: "Total chambre",
    addOns: "Options",
    total: "TOTAL",
    includingAllTaxes: "TOUTES TAXES INCLUSES",
    guaranteeTitle: "Politique de garantie",
    guaranteeBody: "Placeholder. Acompte et règles de paiement seront finalisés lors de l’intégration.",
    cancellationTitle: "Politique d’annulation",
    brsSignaturePrefix: "BRS Signature : ",
    checkInTitle: "Arrivée",
    checkInBody:
      "Placeholder. Arrivée après 15h00. Départ avant 11h00 (Signature : départ tardif 12h00).",
    childrenTitle: "Enfants",
    childrenBody: "Placeholder. Détails politique enfants.",
    petsTitle: "Animaux",
    petsBody: "Placeholder. Détails politique animaux.",
    includingTaxesFees: "Taxes et frais inclus",
  },
  bar: {
    arrivalLong: "Arrivée :",
    arrivalShort: "Arrivée",
    departureLong: "Départ :",
    departureShort: "Départ",
    selectDate: "Choisir la date",
    guest: "Invité",
    guests: "Invités",
    room: "Chambre",
    rooms: "Chambres",
    checkAvailability: "Vérifier la disponibilité et mettre à jour les prix",
    organicBedding: "Literie bio coton dans chaque chambre",
    ensuiteBath: "Salle de bain privée dans toutes les chambres",
    adults: "Adultes",
    children: "Enfants",
    guestCounterAdultSingular: "Adulte",
    guestCounterAdultPlural: "Adultes",
    guestCounterChildSingular: "Enfant",
    guestCounterChildPlural: "Enfants",
    guestCounterChildAgeHint: "(3 - 12 ans)",
    roomLabelTemplate: "Séjour {{n}}",
    addRoom: "+ Ajouter une chambre",
    removeRoom: "Retirer",
    maxRoomsTitle: "Besoin de 6 chambres ou plus ?",
    maxRoomsContactHtml:
      'Contact : <a href="mailto:info@beatenberg-resortspa.ch" class="text-pine-green font-medium underline underline-offset-2">info@beatenberg-resortspa.ch</a>',
  },
  leaveGuard: {
    warning:
      "Vous quittez cette page. Si vous continuez, votre sélection sera effacée.",
    question: "Souhaitez-vous continuer ?",
    stay: "Non",
    leave: "Oui",
  },
  criteriaOverlay: {
    title: "Dates et invités",
    dates: "Dates",
    guests: "Invités",
  },
  splitOccupancy: {
    dividedAcrossRoomsToast:
      "Votre séjour a été réparti sur {{n}} chambres, selon les types de chambres.",
    guidanceUseAddRoomToast:
      "Plus de deux personnes dans une chambre ? Utilisez {{addRoom}} pour réserver une chambre supplémentaire.",
    splitBlockedMaxRooms:
      "Ce nombre d’invités nécessiterait plus de cinq chambres. Contactez-nous ou réduisez le nombre d’invités par chambre.",
    splitBlockedInsufficientAdults:
      "Chaque chambre nécessite au moins un adulte. Ajoutez des adultes ou utilisez {{addRoom}} pour répartir les invités.",
    barApplyRequiresOverlayFirst:
      "Veuillez d’abord confirmer les dates et les invités via le panneau au centre de la page.",
    splitOverflowInlineLead:
      "Votre groupe nécessiterait plus de cinq chambres. Réduisez le nombre d’invités par ligne, ajoutez des lignes avec moins d’invités, ou contactez-nous.",
    splitInsufficientAdultsTitle: "Adultes requis par chambre",
    barSplitGuestHintCompact:
      "Répartissez votre groupe sur plusieurs chambres pour plus de choix de catégories et de vues.",
  },
  confirmation: {
    backHome: "Retour à l’accueil",
    title: "Réservation confirmée",
    subtitle: "Merci pour votre réservation.",
    loadingTitle: "Chargement de votre réservation",
    loadingHint: "Un instant, s’il vous plaît.",
    contactWithNumber: "Contactez-nous avec votre numéro de confirmation si vous l’avez.",
    confirmedTitle: "Votre réservation est confirmée",
    confirmedBodyPrefix: "Merci de nous avoir choisis. Numéro de confirmation",
    confirmedBodySuffix:
      ". Un e-mail de confirmation avec paiement et détails du séjour suivra sous peu.",
    numbersPrefix: "s :",
    stayDetails: "Détails du séjour",
    rooms: "Chambres",
    datesNights: "Dates & nuits",
    guestsLabel: "Invités",
    adult: "adulte",
    adults: "adultes",
    child: "enfant",
    children: "enfants",
    additionalServices: "Services additionnels",
    servicesIncluded: "Inclus dans votre réservation",
    total: "Total",
    reservationCreated: "Réservation créée",
    reservationCreatedBodyPrefix: "Votre réservation est confirmée. Numéro de confirmation",
    reservationCreatedBodySuffix: ". Détails de paiement et confirmation suivront.",
    cardNotAvailableTitle: "Paiement par carte indisponible",
    cardNotAvailableBody:
      "Le paiement sécurisé par carte n’est pas disponible pour cet établissement. Contactez-nous pour finaliser.",
    contactUs: "Nous contacter",
    verifyingPrice: "Vérification du prix final…",
    priceChanged: "Le prix final a changé par rapport au montant indiqué. Vérifiez ci-dessous.",
    finalPrice: "Prix final",
    amountChargeOnConfirmation: "Montant à débiter à la confirmation :",
    paymentDetails: "Coordonnées de paiement",
    paymentSecureNote: "Numéro de carte et CVC sont sécurisés par notre prestataire de paiement.",
    nameOnCard: "Nom sur la carte",
    validity: "Validité",
    tryAnotherCardHint: "Vous pouvez essayer une autre carte ci-dessous. Vos données sont enregistrées.",
    tryAnotherCard: "Essayer une autre carte",
    processing: "Traitement…",
    confirmBooking: "Confirmer la réservation",
    roomFallbackName: "Chambre",
    confirmationNumberShort: "N° {{number}}",
    unknownProduct: "Service additionnel",
  },
  steps: {
    chooseViewTitle: "Planifiez votre séjour",
    chooseViewTitleWithProgress: "Planifiez votre séjour ({{current}}/{{total}})",
    chooseViewActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    stayStepRoomProgress: "Chambre {{current}} sur {{total}}",
    chooseViewSubtitle: "Choisissez votre chambre et votre vue préférées.",
    noRoomsMatch:
      "Aucune chambre ne correspond. Essayez « Toutes les chambres » ou réduisez le nombre d’invités.",
    enhanceTitle: "Enrichissez votre séjour",
    enhanceTitleWithProgress: "Enrichissez votre séjour ({{current}}/{{total}})",
    enhanceSubtitle: "Personnalisez avec des options exclusives.",
    enhanceActiveRoomSubtitle: "{{label}} · {{occupancy}}",
    editStayRoomOccupancyAria: "Modifier le nombre d’invités pour {{label}}",
    editStayRoomOccupancyTitle: "Invités — {{label}}",
    noAddonsCategory: "Aucune option dans cette catégorie.",
    detailsTitle: "Coordonnées & paiement",
    detailsSubtitle: "Saisissez vos informations et confirmez votre réservation.",
    bookingForSelf: "Je réserve pour moi-même",
    bookingForOther: "Je réserve pour quelqu’un d’autre",
    processingReservation: "Traitement de votre réservation",
    doNotClose: "Confirmation en cours — ne fermez pas cette page.",
    yourDetails: "Vos coordonnées",
    guestDetailsSection: "Coordonnées du client",
    firstName: "Prénom *",
    lastName: "Nom *",
    email: "E-mail *",
    emailHint: "Nous enverrons une confirmation à cette adresse.",
    phone: "Téléphone",
    nationality: "Nationalité",
    specialRequests: "Demandes particulières",
    marketingOptIn: "Je souhaite recevoir offres et actualités par e-mail",
    marketingOptInBooker: "Je souhaite recevoir offres et actualités par e-mail (réservant)",
    backAria: "Retour",
  },
  summary: {
    title: "Récapitulatif du séjour",
    chooseRoom: "Choisir une chambre",
    chooseRate: "Choisir un tarif",
    edit: "Modifier",
    stayEnhancements: "Extras séjour",
    enhancementDiscountNoRate: "10 à 15 % de réduction sur toutes les prestations du resort",
    enhancementDiscountSignature:
      "15 % de réduction sur toutes les prestations du resort pendant votre séjour.",
    enhancementDiscountStandard:
      "10 % de réduction sur toutes les prestations du resort réservées en ligne",
    noAddons: "Aucune option sélectionnée.",
    exclusiveBenefits: "Avantages exclusifs",
    benefit1Title: "Offres et inventaire exclusifs en réservation directe",
    benefit1Desc: "Meilleurs prix et disponibilité en réservant directement",
    benefit2Title: "Petit-Déjeuner Continental Inclus",
    benefit2Desc: "Commencez chaque matin avec notre petit-déjeuner",
    benefit3Title: "Garantie du meilleur prix",
    benefit3Desc: "Réservez en direct pour nos meilleurs tarifs et forfaits",
    continue: "Continuer",
    back: "Retour",
    selectRoomRateContinue: "Choisissez une chambre et un tarif pour continuer.",
    selected: "Sélectionné",
    selectRatePlan: "Choisir un tarif",
    closeExpandedSummaryAria: "Fermer le récapitulatif du séjour",
    multiRoomPlanStickyHeading: "Vos chambres",
    roomPlanNotSelected: "Non sélectionné",
    mobileMultiRoomStayProgress: "Séjour ({{k}}/{{n}})",
    mobileCollapsedDetailsTotalNightsLine: "Total : {{count}} {{nightsLabel}}",
    mobileFreeCancellationPillTillDate: "jusqu'au {{date}}",
    expandedSheetCheckoutLink: "Commander",
    toggleMultiRoomAddonsAria: "Afficher ou masquer les options pour ce séjour",
  },
  bestRateGuarantee: {
    headline: "Meilleur tarif garanti",
    subline: "Lorsque vous réservez en direct avec nous.",
    learnMore: "En savoir plus",
  },
  carousel: {
    goToImage: "Aller à l’image",
    viewFullScreen: "Photos plein écran",
    photosTitle: "photos",
    photosDescription: "Faites défiler les photos de la chambre.",
  },
  roomLadder: bookRoomLadderCopyFr,
  upsells: bookUpsellsCopyFr,
  roomTags: bookRoomTagsFr,
  upsellCategoryLabels: {
    Dining: "Restauration",
    Romance: "Romantique",
    "Spa & Massage": "Spa & massage",
    "Sauna & Thermal": "Sauna & thermes",
  },
  upsellCard: {
    decreaseAria: "Diminuer la quantité",
    increaseAria: "Augmenter la quantité",
    add: "Ajouter au séjour",
    added: "Ajouté",
    pricedForRoomGuests: "Tarif basé sur les invités de cette chambre.",
  },
};
